import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Storage } from 'aws-amplify';
import * as naturalCompare from 'natural-compare-lite';
import { LoadStatus } from '../../constants/constants';

const catalogList = [
  'catalogs/iso27001_2013.json',
  'catalogs/iso27002_2013.json',
  'catalogs/iso27001_2022.json',
  'catalogs/iso27002_2022.json',
  'catalogs/soc2_2017.json',
];

export const initialize = createAsyncThunk('catalogs/initialize', async () => {
  const initialList = [];
  const requests = catalogList.reduce((list, key) => list.concat(Storage.get(key, { download: true })), initialList);
  const blobList = [];
  const blobs = await Promise.all(requests, setTimeout(6000));
  const transforms = blobs.reduce((list, entry) => list.concat(entry.Body.text()), blobList);
  const jsonList = await Promise.all(transforms, setTimeout(6000));
  return jsonList.map((entry) => JSON.parse(entry));
});

const handleGroup = (group) => {
  const rval = {};
  group.controls?.forEach((control) => {
    rval[control.id] = `${control.label}: ${control.title}`;
  });
  group.groups?.forEach((subGroup) => {
    Object.assign(rval, handleGroup(subGroup));
  });
  return rval;
};

export const catalogsSlice = createSlice({
  name: 'catalogs',
  initialState: {
    catalogList: [],
    catalogLoadStatus: LoadStatus.INITIAL,
    controlTitles: {},
  },
  extraReducers: (builder) => {
    builder
      .addCase(initialize.pending, (state, _) => {
        state.catalogLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(initialize.rejected, (state, action) => {
        state.catalogLoadStatus = LoadStatus.FAILURE;
      })
      .addCase(initialize.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.catalogList = action.payload.sort((a, b) => {
            if (a.info.version !== b.info.version) {
              return a.version - b.version;
            }
            return naturalCompare(a.info.type, b.info.type);
          });
          const controlTitles = {};
          action.payload.forEach((catalog) => {
            Object.assign(controlTitles, handleGroup(catalog));
          });
          state.controlTitles = controlTitles;
          state.catalogLoadStatus = LoadStatus.SUCCESS;
        }
      });
  },
});

const { reducer } = catalogsSlice;

export default reducer;

export const selectCatalogsLoadStatus = (state) => state.catalogs.catalogLoadStatus;

export const selectAllCatalogs = (state) => state.catalogs.catalogList;

export const selectTitle = (state, id) => {
  const splitId = id.split(':');
  const type = splitId[0];
  const version = splitId[1];
  const catalog = state.catalogs.catalogList.find((entry) => entry.info.type === type && entry.info.version === version);
  if (splitId.length === 2) {
    return catalog.info.title;
  }
  const prefix = splitId.slice(0, 2).join(':');
  const idParts = splitId.slice(-1)[0].split('.');
  if (idParts.length === 1) {
    const result = catalog.groups.find((group) => group.id === id);
    return result ? `${result.label}: ${result.title}` : 'Not found';
  }
  const idArray = idParts.map((_, index, parts) => `${prefix}:${parts.slice(0, index + 1).join('.')}`);
  let found = null;
  let current = { groups: catalog.groups, controls: [] };
  idArray.forEach((entry, index) => {
    if (index === idArray.length - 1) {
      const result = current.controls
        .find((item) => item.id === entry) || current.groups.find((item) => item.id === entry) || null;
      found = result ? `${result.label}: ${result.title}` : 'Not found';
    } else {
      const findNext = current.groups
        .find((group) => group.id === entry) || current.controls.find((group) => group.id === entry);
      current = { groups: findNext.groups || [], controls: findNext.controls || [] };
    }
  });
  return found || 'Not found';
};

export const selectMultipleTitles = (state, ids) => {
  const foundList = ids.map((id) => {
    const splitId = id.split(':');
    const type = splitId[0];
    const version = splitId[1];
    const catalog = state.catalogs.catalogList.find((entry) => entry.info.type === type && entry.info.version === version);
    if (splitId.length === 2) {
      return catalog.info.title;
    }
    const prefix = splitId.slice(0, 2).join(':');
    const idParts = splitId.slice(-1)[0].split('.');
    if (idParts.length === 1) {
      const result = catalog.groups.find((group) => group.id === id);
      return result ? `${result.label}: ${result.title}` : 'Not found';
    }
    const idArray = idParts.map((_, index, parts) => `${prefix}:${parts.slice(0, index + 1).join('.')}`);
    let found = null;
    let current = { groups: catalog.groups, controls: [] };
    idArray.forEach((entry, index) => {
      if (index === idArray.length - 1) {
        const result = current.controls
          .find((item) => item.id === entry) || current.groups.find((item) => item.id === entry) || null;
        found = result ? `${result.label}: ${result.title}` : 'Not found';
      } else {
        const findNext = current.groups
          .find((group) => group.id === entry) || current.controls.find((group) => group.id === entry);
        current = { groups: findNext.groups || [], controls: findNext.controls || [] };
      }
    });
    return found || 'Not found';
  });
  return foundList.join(', ');
};

export const selectDescription = (state, id) => {
  const splitId = id.split(':');
  const type = splitId[0];
  const version = splitId[1];
  const catalog = state.catalogs.catalogList.find((entry) => entry.info.type === type && entry.info.version === version);
  if (splitId.length === 2) {
    return catalog.info.description;
  }
  const prefix = splitId.slice(0, 2).join(':');
  const idParts = splitId.slice(-1)[0].split('.');
  if (idParts.length === 1) {
    const result = catalog.groups.find((group) => group.id === id);
    return result.description;
  }
  const idArray = idParts.map((_, index, parts) => `${prefix}:${parts.slice(0, index + 1).join('.')}`);
  let found = null;
  let current = { groups: catalog.groups, controls: [] };
  idArray.forEach((entry, index) => {
    if (index === idArray.length - 1) {
      const result = current.controls
        .find((item) => item.id === entry) || current.groups.find((item) => item.id === entry) || null;
      found = result.description;
    } else {
      const findNext = current.groups
        .find((group) => group.id === entry) || current.controls.find((group) => group.id === entry);
      current = { groups: findNext.groups || [], controls: findNext.controls || [] };
    }
  });
  return found || '';
};
